<template>
  <div class="post-container">
    <el-row :gutter="20">
      <el-col :lg="12" :md="24">
        <div class="channel-container">
          <span class="post-head">Channels</span>
          <el-button
            v-if="channels.length && !editPost"
            class="pull-right"
            :type="isAllChecked ? 'danger' : ''"
            size="mini"
            @click="handleSelectAll"
            plain
            >{{ isAllChecked ? 'Clear' : 'Select' }} All</el-button
          >

          <div class="channels">
            <el-checkbox-group
              v-if="channels.length"
              v-model="checkedChannels"
              @change="handleCheckedChannelChange"
            >
              <div v-for="channel in channels" :key="channel.id">
                <el-checkbox :label="channel.id" :disabled="isChannelDisabled(channel)">
                  <social-icon
                    class="channel-icon"
                    :type="channel.channel_type_title"
                  ></social-icon>
                  {{ channel.title }}
                </el-checkbox>
              </div>
            </el-checkbox-group>
            <p v-else class="text-center text-muted">No Social Channels</p>
          </div>
        </div>
      </el-col>

      <el-col :lg="12" :md="24">
        <div class="message-container">
          <span class="post-head">Message</span>
          <el-input
            class="post-message"
            type="textarea"
            resize="none"
            ref="messageInput"
            :autosize="{ minRows: 6, maxRows: 6 }"
            placeholder="Post Message..."
            v-model="messageContent"
            @blur="handleInputBlur"
          ></el-input>

          <el-popover placement="right" trigger="click" ref="emojiPicker">
            <picker
              class="emoji-picker"
              :pickerStyles="pickerStyles"
              :showPreview="false"
              :infiniteScroll="false"
              :sheetSize="32"
              :exclude="['recent']"
              @select="insertEmoji"
            />
          </el-popover>
        </div>

        <div class="message-controls">
          <el-tooltip class="item" effect="dark" content="Insert Emoticon" placement="bottom">
            <i class="message-control fa fa-smile-o" v-popover:emojiPicker></i>
          </el-tooltip>
        </div>
      </el-col>
    </el-row>
    <el-row>
      <el-col :lg="12" :md="24">
        <div class="post-schedule">
          <el-date-picker
            type="datetime"
            placeholder="Scheduled Post"
            :picker-options="datePickerOptions"
            v-model="postSchedule"
            :disabled="getSelectedTikTokChannels.length > 0"
          ></el-date-picker>
        </div>
      </el-col>
      <el-col :lg="12" :md="24">
        <div class="post-buttons">
          <span>
            <el-button @click="addMedia = !addMedia" icon="fa fa-plus">&nbsp;Add Media</el-button>

            <el-tooltip
              :content="tiktokMediaWarning"
              placement="top"
              :disabled="!(getSelectedTikTokChannels.length > 0 && mediaList.length === 0)"
            >
              <span>
                <el-button
                  v-if="!sending"
                  type="primary"
                  @click="handleSendPost"
                  :disabled="isPostDisabled"
                  plain
                >
                  {{ editPost ? 'Update' : schedule ? 'Schedule' : 'Send' }}&nbsp;
                  <i
                    :class="{
                      'fa fa-paper-plane': !editPost && !schedule,
                      'fa fa-calendar-o': !editPost && schedule,
                    }"
                  ></i>
                </el-button>

                <el-button v-else type="primary" :disabled="true" plain>
                  Working&nbsp;
                  <i class="fa fa-spinner fa-pulse"></i>
                </el-button>
              </span>
            </el-tooltip>
          </span>

          <span v-if="editPost">
            <el-tooltip content="Delete Post" placement="bottom">
              <el-button
                type="danger"
                @click="handleDeletePost"
                :icon="deleting ? 'fa fa-spinner fa-pulse' : 'fa fa-trash'"
                plain
              ></el-button>
            </el-tooltip>
          </span>
        </div>
      </el-col>
    </el-row>
    <el-row v-if="getSelectedTikTokChannels.length > 0">
      <hr />
      <el-col :lg="24">
        <div ref="tiktokOptionsWrapper">
          <TikTokOptions :settings="tiktokSettings" />
        </div>
      </el-col>
    </el-row>
    <el-row>
      <hr v-show="addMedia" />
      <el-upload
        v-show="addMedia"
        action="https://jsonplaceholder.typicode.com/posts/"
        :accept="getSelectedTikTokChannels.length > 0 ? 'video/mp4' : 'image/*, video/*'"
        list-type="text"
        class="post-file-upload"
        :on-change="handleFileChange"
        :on-remove="handleFileRemove"
        :file-list="fileList"
        :auto-upload="false"
        drag
      >
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">
          Drop file here or
          <em>click to upload</em>
        </div>
      </el-upload>
    </el-row>
    <el-row v-if="mediaList.length">
      <hr />
      <p>Attached Media:</p>
      <video
        v-if="mediaList[0].filetype && mediaList[0].filetype.includes('video')"
        :src="`data:${mediaList[0].filetype};base64,${mediaList[0].base64}`"
        controls
        height="150"
      ></video>
      <img
        v-else
        :src="`data:${mediaList[0].filetype};base64,${mediaList[0].base64}`"
        height="100"
      />
    </el-row>
  </div>
</template>

<script>
import { Picker } from 'emoji-mart-vue';
import moment from 'moment';
import SocialIcon from '@/components/common/Icons/SocialIcon';
import TikTokOptions from '@/components/social/tiktok/TikTokOptions';
import collect from 'collect.js';
import { getBase64FileObj } from '@/helpers/file';

const disabledDate = time => time < moment().subtract(1, 'day').toDate();

export default {
  name: 'SocialPost',

  components: {
    SocialIcon,
    Picker,
    TikTokOptions,
  },

  props: {
    channels: {
      type: Array,
      default: [],
    },
    post: {
      type: Object,
      required: false,
    },
    editPost: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    sending: false,
    deleting: false,
    addMedia: false,
    isAllChecked: false,
    fileList: [],
    checkedChannels: [],
    mediaList: [],
    schedule: null,
    messageContent: '',
    messageCursorPos: 0,
    datePickerOptions: {
      disabledDate,
    },
    pickerStyles: {
      height: '330px',
      borderRadius: '4px',
    },
    isLoadingTikTokCreator: false,
    tiktokSettings: {
      max_video_post_duration_sec: '',
      privacy_level_options: [],
      creator_avatar_url: '',
      creator_nickname: '',
      creator_username: '',
      allow_comment: false,
      allow_duet: false,
      allow_stitch: false,
    },
  }),

  computed: {
    postSchedule: {
      set(val) {
        this.schedule = val ? String(val.getTime() / 1000) : null;
      },
      get() {
        return this.schedule ? new Date(parseInt(this.schedule, 10) * 1000) : null;
      },
    },
    getSelectedTwitterChannels() {
      const selectedChannels = this.checkedChannels;
      const filteredTwitterChannelSelected = collect(this.channels)
        .whereIn('id', selectedChannels)
        .filter(channel => channel.channel_type_title.toLowerCase() === 'twitter')
        .all();
      return filteredTwitterChannelSelected;
    },
    showImagePreview() {
      return (
        this.editPost &&
        this.mediaList.length &&
        this.mediaList.reduce((show, x) => show || !!x.url, false)
      );
    },
    getSelectedTikTokChannels() {
      const selectedChannels = this.checkedChannels;
      const filteredTikTokChannels = collect(this.channels)
        .whereIn('id', selectedChannels)
        .filter(channel => channel.channel_type_title.toLowerCase() === 'tiktok')
        .all();
      return filteredTikTokChannels;
    },
    isPostDisabled() {
      const hasTikTok = this.getSelectedTikTokChannels.length > 0;

      return (
        (hasTikTok && this.mediaList.length === 0) ||
        (hasTikTok && !this.tiktokSettings.privacy_level) ||
        (hasTikTok && this.schedule)
      );
    },
    tiktokMediaWarning() {
      if (!this.getSelectedTikTokChannels.length) return '';

      const rules = [
        {
          condition: this.schedule,
          message: 'Scheduling TikTok posts is not supported',
        },
        {
          condition: !this.tiktokSettings.privacy_level,
          message: 'Please select who can view this video on TikTok',
        },
        {
          condition: this.mediaList.length === 0,
          message: 'Please add a video to post on TikTok',
        },
      ];

      const match = rules.find(r => r.condition);
      return match ? match.message : '';
    },
  },

  methods: {
    handleSelectAll() {
      const allChecked = this.checkedChannels.length === this.channels.length;
      this.checkedChannels = allChecked ? [] : [...this.channels.map(x => x.id)];
      this.isAllChecked = this.checkedChannels.length === this.channels.length;
    },

    insertEmoji(emoji) {
      const cursorPos = this.messageCursorPos;
      this.messageContent = `${this.messageContent.slice(0, cursorPos)}${
        emoji.native
      }${this.messageContent.slice(cursorPos)}`;
    },

    isVideo(file) {
      return file.type && file.type.startsWith('video');
    },

    handleInputBlur(e) {
      this.messageCursorPos = e.target.selectionStart;
    },

    handleCheckedChannelChange(val) {
      const checkedCount = val.length;
      this.isAllChecked = checkedCount === this.channels.length;
    },

    handleSendPost() {
      if (this.getSelectedTikTokChannels.length > 0 && !this.tiktokSettings.privacy_level) {
        this.$alert('Please select a privacy level for TikTok before sending.', 'Missing Privacy', {
          type: 'warning',
          confirmButtonText: 'OK',
        });
        return;
      }

      if (this.checkedChannels.length === 0 || !this.messageContent) return;

      if (this.schedule && this.schedule < Date.now() / 1000) {
        const opts = {
          type: 'warning',
          title: 'Time in past',
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
        };

        this.$confirm('Scheduled date has already elapsed. Send imediately?', opts)
          .then(this.sendPost)
          .catch(() => null);
      } else {
        this.sendPost();
      }
    },

    sendPost() {
      if (this.schedule < Date.now() / 1000) this.schedule = null;

      this.sending = true;
      this.$emit(this.editPost ? 'update' : 'send', {
        id: this.post ? this.post.id : null,
        channelIds: this.checkedChannels,
        message: this.messageContent,
        media: this.mediaList,
        scheduleTimestamp: this.schedule,
        settings: this.getSelectedTikTokChannels.length ? { ...this.tiktokSettings } : null,
      });
    },
    validateFileSize(file) {
      const bytes = 1024;
      const fileSize = file.size / bytes / bytes; // in MB
      let isValidFileSize = true;
      const selectedTwitterChannels = this.getSelectedTwitterChannels;
      if (selectedTwitterChannels.length > 0) {
        if (fileSize > 0.1 && file.name.match(/.(jpg|jpeg|png)$/i)) {
          this.$alert('File size exceeds 5MB maximum limit for an image');
          isValidFileSize = false;
        }
        if (fileSize > 15) {
          this.$alert('File size exceeds 15MB maximum limit for a gif or video');
          isValidFileSize = false;
        }
      }

      return file && isValidFileSize;
    },
    handleFileChange(file) {
      if (!this.validateFileSize(file)) {
        this.handleFileRemove();
        return;
      }

      // Validate video duration for TikTok
      if (this.getSelectedTikTokChannels.length > 0 && file.raw.type.startsWith('video')) {
        const maxDuration = this.tiktokSettings.max_video_post_duration_sec;
        if (maxDuration) {
          const video = document.createElement('video');
          video.preload = 'metadata';
          video.onloadedmetadata = () => {
            URL.revokeObjectURL(video.src);
            if (video.duration > maxDuration) {
              this.$alert(
                `Video exceeds TikTok's maximum duration of ${this.formatDuration(maxDuration)}.`,
                'Video Too Long',
                { type: 'warning' },
              );
              this.handleFileRemove();
              return;
            }
            this.loadFileToMediaList(file);
          };
          video.src = URL.createObjectURL(file.raw);
          return;
        }
      }

      this.loadFileToMediaList(file);
    },

    loadFileToMediaList(file) {
      this.fileList = [file];
      getBase64FileObj(file.raw)
        .then(fileObj => {
          this.mediaList = [fileObj];
        })
        .catch(() => this.$alert('Unable to read file', { type: 'error' }));
    },
    formatDuration(seconds) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
      return `${minutes} minute${minutes > 1 ? 's' : ''}`;
    },
    isChannelDisabled(channel) {
      const isTikTok = channel.channel_type_title.toLowerCase() === 'tiktok';
      const hasTikTokSelected = this.getSelectedTikTokChannels.length > 0;
      const hasOtherSelected = this.checkedChannels.some(id => {
        const ch = this.channels.find(c => c.id === id);
        return ch && ch.channel_type_title.toLowerCase() !== 'tiktok';
      });

      if (isTikTok) return hasOtherSelected;
      return hasTikTokSelected;
    },
    handleFileRemove() {
      this.fileList = [];
      this.mediaList = [];
    },

    handleDeletePost() {
      this.$confirm('Are you sure you would like to delete this post?').then(() => {
        this.deleting = true;
        this.$emit('delete', this.post.id);
      });
    },

    fetchTikTokCreatorInfo(channel) {
      this.isLoadingTikTokCreator = true;

      this.$store
        .dispatch('channel/getTiktokCreatorInfo', {
          channel_handle: channel.handle,
          company_id: channel.company,
        })
        .then(data => {
          this.tiktokSettings = {
            max_video_post_duration_sec: data.max_video_post_duration_sec,
            privacy_level_options: data.privacy_level_options,
            creator_avatar_url: data.creator_avatar_url,
            creator_nickname: data.creator_nickname,
            creator_username: data.creator_username,
            allow_comment: !data.comment_disabled,
            allow_duet: !data.duet_disabled,
            allow_stitch: !data.stitch_disabled,
          };
        })
        .catch(() => {
          this.$alert('Failed to fetch TikTok creator info', 'Error', { type: 'error' });
          this.resetTikTokSettings();
        })
        .finally(() => {
          this.isLoadingTikTokCreator = false;
        });
    },

    resetTikTokSettings() {
      this.tiktokSettings = {
        max_video_post_duration_sec: '',
        privacy_level_options: [],
        creator_avatar_url: '',
        creator_nickname: '',
        creator_username: '',
        allow_comment: false,
        allow_duet: false,
        allow_stitch: false,
      };
    },
  },

  mounted() {
    if (this.post) {
      this.mediaList = this.post.media || [];
      this.post.channels = this.post.channels || [];
      this.checkedChannels = this.post.channels;
      this.schedule = new Date(this.post.post_date).getTime() / 1000;
      this.messageContent = this.post.message;
      this.allChecked = this.channels.reduce((acc, x) => {
        if (this.post.channels.findIndex(p => x === p) !== -1) return false;
        return acc;
      }, true);
    }
  },

  watch: {
    getSelectedTikTokChannels: {
      handler(channels) {
        if (channels.length === 1) {
          this.fetchTikTokCreatorInfo(channels[0]);
        } else if (channels.length === 0) {
          this.resetTikTokSettings();
        }
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/style/_variables_live.scss';

.channels {
  width: auto;
  margin: 8px 0;
  padding: 8px 25px;
  border: 1px solid #ccc;
  border-radius: 4px;
  height: 138px;
  overflow-y: scroll;
}

.post-head {
  font-size: 1.3em;
}

.channel-icon {
  display: inline;
}

.post-message {
  min-height: 150px;
  height: 150px;
  margin: 8px 0;
  overflow-y: scroll;
}

.text-center {
  text-align: center;
}

.text-muted {
  color: lightgray;
}

.post-schedule {
  float: left;
}

.post-buttons {
  float: right;
}

.message-controls {
  float: right;
  margin-top: 0;
  margin-bottom: 32px;
}

.message-control {
  cursor: pointer;
  font-size: 2em;
  padding-top: 0;
  margin-top: -15px;
  color: lightgray;

  &:hover {
    color: $colony-primary;
    opacity: 0.7;
  }
}
</style>



// WEBPACK FOOTER //
// SocialPost.vue?7b697c89